import React, { useEffect, useState } from 'react';
import { Plus, Loader2, Trash2, CheckCircle2, MessageSquareWarning, Clock, Paperclip } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { useToast } from '../../hooks/use-toast';
import { clientService } from '../../api';

export const SIGNOFF_STATUS = {
  pending: { label: 'Awaiting client', icon: Clock, cls: 'bg-amber-50 text-amber-800 ring-amber-200' },
  approved: { label: 'Approved', icon: CheckCircle2, cls: 'bg-emerald-50 text-emerald-800 ring-emerald-200' },
  changes_requested: { label: 'Changes requested', icon: MessageSquareWarning, cls: 'bg-red-50 text-red-700 ring-red-200' },
};

const NewSignoffDialog = ({ open, onOpenChange, clientId, onCreated }) => {
  const { toast } = useToast();
  const [form, setForm] = useState({ title: '', description: '', file: null });
  const [busy, setBusy] = useState(false);
  const submit = (e) => {
    e.preventDefault();
    setBusy(true);
    clientService.createSignoff(clientId, form).then(() => {
      toast({ title: 'Sent for sign-off', description: 'The client can approve it from their portal.' });
      setForm({ title: '', description: '', file: null });
      onOpenChange(false);
      onCreated();
    }).catch((err) => toast({ title: 'Could not create', description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' }))
      .finally(() => setBusy(false));
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>Request client sign-off</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div><Label htmlFor="so-title">What needs approval?</Label><Input id="so-title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="2026 Annual Financial Statements" className="mt-1.5" data-testid="signoff-title-input"/></div>
          <div><Label htmlFor="so-desc">Note to client</Label><Textarea id="so-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Please review and approve before we submit to SARS." className="mt-1.5" data-testid="signoff-description-input"/></div>
          <div><Label htmlFor="so-file">Attach document (optional)</Label><Input id="so-file" type="file" onChange={(e) => setForm({ ...form, file: e.target.files?.[0] || null })} className="mt-1.5" data-testid="signoff-file-input"/></div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={busy} className="bg-emerald-800 hover:bg-emerald-900 text-white" data-testid="signoff-submit-btn">{busy && <Loader2 size={14} className="mr-2 animate-spin"/>} Send to client</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const SignoffRow = ({ s, onDelete }) => {
  const st = SIGNOFF_STATUS[s.status];
  return (
    <div className="px-5 py-4" data-testid={`signoff-${s.id}`}>
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="font-medium text-stone-900">{s.title}</div>
          {s.description && <div className="text-xs text-stone-600 mt-0.5">{s.description}</div>}
          <div className="text-[11px] text-stone-400 mt-1">Sent {new Date(s.createdAt).toLocaleDateString()} by {s.createdByName || 'staff'}
            {s.fileId && <> · <a href={clientService.fileUrl(s.fileId)} target="_blank" rel="noreferrer" className="text-emerald-800 hover:underline inline-flex items-center gap-0.5"><Paperclip size={10}/>{s.fileName}</a></>}
          </div>
        </div>
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ring-1 ${st.cls}`} data-testid={`signoff-status-${s.id}`}><st.icon size={12}/> {st.label}</span>
        <button type="button" onClick={() => onDelete(s)} className="text-stone-400 hover:text-red-600" data-testid={`signoff-delete-${s.id}`}><Trash2 size={15}/></button>
      </div>
      {s.response && (
        <div className="mt-3 rounded-lg bg-stone-50 border border-stone-100 p-3 text-xs text-stone-700" data-testid={`signoff-response-${s.id}`}>
          <div><strong>{s.response.name}</strong> · {new Date(s.response.at).toLocaleString()} · IP {s.response.ip || 'unknown'}</div>
          {s.response.comment && <div className="mt-1 italic">&ldquo;{s.response.comment}&rdquo;</div>}
        </div>
      )}
    </div>
  );
};

export const SignoffsPanel = ({ clientId }) => {
  const { toast } = useToast();
  const [list, setList] = useState(null);
  const [open, setOpen] = useState(false);
  const load = () => clientService.signoffs(clientId).then(setList);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [clientId]);
  const remove = (s) => { if (window.confirm(`Delete "${s.title}"?`)) clientService.deleteSignoff(s.id).then(() => { toast({ title: 'Sign-off deleted' }); load(); }); };

  return (
    <div className="rounded-xl bg-white border border-stone-200 overflow-hidden" data-testid="signoffs-panel">
      <div className="p-5 border-b border-stone-100 flex items-center justify-between gap-3">
        <div>
          <div className="font-semibold text-stone-900">Client sign-offs</div>
          <div className="text-xs text-stone-500 mt-0.5">Clients approve or request changes in their portal. Name, time and IP are recorded.</div>
        </div>
        <Button size="sm" onClick={() => setOpen(true)} className="bg-emerald-800 hover:bg-emerald-900 text-white" data-testid="new-signoff-btn"><Plus size={14} className="mr-1.5"/> Request sign-off</Button>
      </div>
      {!list && <div className="p-8 flex justify-center"><Loader2 className="animate-spin text-stone-400" size={18}/></div>}
      {list && list.length === 0 && <div className="p-8 text-sm text-stone-500 text-center" data-testid="signoffs-empty">Nothing sent for approval yet.</div>}
      <div className="divide-y divide-stone-100">{list?.map((s) => <SignoffRow key={s.id} s={s} onDelete={remove}/>)}</div>
      <NewSignoffDialog open={open} onOpenChange={setOpen} clientId={clientId} onCreated={load}/>
    </div>
  );
};
